import { CATEGORY_LABELS } from './findings-url';
import {
  findingFixContextFromJson,
  parseJsonRecord,
} from './finding-detail-model';
import { formatRuleLabel } from '@/lib/govern-rule-catalog';
import { isSafeAutofixCandidate } from '@/lib/govern/autofix-actionability';
import type { FindingDoc, PrDecisionRow } from './findings-types';

export interface DuplicateParts {
  value: string;
  tokenNames: string[];
}

export type ResolvedSuggestion = {
  kind: 'token' | 'prose';
  display: string;
  copyText: string | null;
} | null;

export function formatCategoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat;
}

export function stripCategoryPrefix(message: string): string {
  return message.replace(/^\[\w+]\s*/, '');
}

export function parseDuplicateMessage(message: string): DuplicateParts | null {
  const newFormat = message.match(
    /^Duplicate tokens:\s*(--[\w-]+(?:,\s*--[\w-]+)+)\s+all resolve to\s+`([^`]+)`$/,
  );
  if (newFormat) {
    return {
      tokenNames: newFormat[1].split(/,\s*/),
      value: newFormat[2],
    };
  }

  const oldFormat = message.match(
    /^\d+\s+tokens?\s+share\s+value\s+(.+?):\s*(--[\w-]+(?:,\s*--[\w-]+)*)$/,
  );
  if (!oldFormat) return null;
  return {
    value: oldFormat[1],
    tokenNames: oldFormat[2].split(/,\s*/),
  };
}

export function disambiguatedPath(filePath: string): string {
  const parts = filePath.split('/');
  return parts.length >= 2 ? parts.slice(-2).join('/') : filePath;
}

const COLOR_RE =
  /^(#[0-9a-f]{3,8}|(?:rgb|hsl|oklch|lch|lab|oklab|color|hwb)a?\s*\(.+\))$/i;

export function isColorValue(value: string): boolean {
  return COLOR_RE.test(value.trim());
}

export function buildGitHubUrl(
  repoFullName: string | undefined,
  commitSha: string | undefined,
  filePath: string,
  line?: number,
): string | null {
  if (!repoFullName || !commitSha) return null;
  const base = `https://github.com/${repoFullName}/blob/${commitSha}/${filePath}`;
  return line != null ? `${base}#L${line}` : base;
}

export function severityBreakdown(counts: {
  error: number;
  warning: number;
  info: number;
}): string {
  const parts: string[] = [];
  const total = counts.error + counts.warning + counts.info;
  for (const sev of ['error', 'warning', 'info'] as const) {
    const n = counts[sev];
    if (!n) continue;
    const label =
      sev === 'error' ? 'errors' : sev === 'warning' ? 'warnings' : 'info';
    parts.push(`${n} ${n === 1 ? label.replace(/s$/, '') : label}`);
  }
  return parts.join(' · ') || `${total} findings`;
}

export function canonicalSeverity(
  severity: string,
): 'critical' | 'serious' | 'moderate' | 'minor' {
  if (severity === 'critical') return 'critical';
  if (severity === 'error' || severity === 'serious') return 'serious';
  if (severity === 'warning' || severity === 'moderate') return 'moderate';
  return 'minor';
}

export function severityLabel(severity: string): string {
  const canonical = canonicalSeverity(severity);
  return canonical.charAt(0).toUpperCase() + canonical.slice(1);
}

export function topCategoryItems(
  counts: Record<string, number>,
  limit = 3,
): Array<{ category: string; label: string; count: number }> {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([category, count]) => ({
      category,
      label: formatCategoryLabel(category).toLowerCase(),
      count,
    }));
}

export function classNames(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function formatCompactCount(count: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact' }).format(count);
}

export function plural(
  count: number,
  singular: string,
  pluralLabel = `${singular}s`,
) {
  return `${formatCompactCount(count)} ${count === 1 ? singular : pluralLabel}`;
}

export function prImpactLabel(row: PrDecisionRow): string {
  if (row.impact.introduced > 0) {
    return `${plural(row.impact.introduced, 'introduced', 'introduced')}`;
  }
  if (row.impact.existing > 0) {
    return `${plural(row.impact.existing, 'existing', 'existing')}`;
  }
  if (row.impact.resolved > 0) {
    return `${plural(row.impact.resolved, 'resolved', 'resolved')}`;
  }
  if (row.impact.suppressed > 0) {
    return `${plural(row.impact.suppressed, 'suppressed', 'suppressed')}`;
  }
  return plural(row.occurrenceCount, 'occurrence');
}

export function governanceLabel(actionability: string): string {
  if (actionability === 'safe_fix') return 'Safe fix';
  if (actionability === 'guided_fix') return 'Guided fix';
  if (actionability === 'policy_needed') return 'Policy needed';
  if (actionability === 'manual_review') return 'Manual review';
  if (actionability === 'blocked') return 'Blocked';
  return actionability;
}

export function prReviewStatusLabel(status: PrDecisionRow['reviewStatus']) {
  if (status === 'approved') return 'Approved';
  if (status === 'changes_requested') return 'Fix requested';
  if (status === 'fix_created') return 'Fix PR created';
  if (status === 'commented') return 'Commented';
  if (status === 'issue_linked') return 'Issue linked';
  if (status === 'policy_updated') return 'Policy updated';
  return 'Unreviewed';
}

export function prDecisionActionLabel(
  action: NonNullable<PrDecisionRow['latestDecisionAction']>,
) {
  if (action.kind === 'approved') return 'Approved';
  if (action.kind === 'requested_fix') return 'Fix requested';
  if (action.kind === 'autofix_created') return 'Fix PR created';
  if (action.kind === 'commented') return 'PR comments posted';
  if (action.kind === 'linked_issue') return 'Issue linked';
  if (action.kind === 'converted_to_policy') return 'Policy updated';
  return action.kind;
}

export function findingSourceLabel(source: string | undefined): string {
  if (source === 'ci') return 'CI / CD';
  if (source === 'manual') return 'Manual';
  if (source === 'mixed') return 'Mixed sources';
  return 'Repo scan';
}

export function findingSourceSummary(
  source: string | undefined,
  prNumber?: number,
): string {
  const label = findingSourceLabel(source);
  return source === 'ci' && prNumber ? `${label} · PR #${prNumber}` : label;
}

export function findingLifecycleLabel(status: string): string {
  if (status === 'open') return 'Current finding';
  if (status === 'resolved') return 'Historical · resolved';
  if (status === 'ignored') return 'Historical · ignored';
  return status ? `Historical · ${status}` : 'Historical';
}

export function findingStatusLabel(status: string): string {
  if (status === 'open') return 'Open';
  if (status === 'resolved') return 'Resolved';
  if (status === 'ignored') return 'Ignored';
  return status || 'Unknown';
}

export function prImpactParts(impact: PrDecisionRow['impact']) {
  return [
    { key: 'introduced', label: 'Introduced', count: impact.introduced },
    { key: 'existing', label: 'Existing', count: impact.existing },
    { key: 'resolved', label: 'Resolved', count: impact.resolved },
    { key: 'suppressed', label: 'Suppressed', count: impact.suppressed },
  ] as const;
}

export function formatPrUpdatedAt(value: string | number | undefined): string {
  if (!value) return 'No scan yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No scan yet';
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function resolveSuggestion(f: FindingDoc): ResolvedSuggestion {
  const isToken = f.suggestedToken?.startsWith('--');

  // When a token replacement is available, render the bare token name
  // (e.g. `var(--docs-space-3)`). The verbose `f.suggestion` prose
  // ("Replace `12px` with `var(...)`") would duplicate the rawValue
  // already shown on the left of the arrow, so we ignore it here.
  if (f.suggestedToken && isToken) {
    return {
      kind: 'token',
      display: `var(${f.suggestedToken})`,
      copyText: `var(${f.suggestedToken})`,
    };
  }

  if (f.suggestion) {
    return {
      kind: 'prose',
      display: f.suggestion,
      copyText: null,
    };
  }

  if (f.suggestedToken) {
    return { kind: 'prose', display: f.suggestedToken, copyText: null };
  }

  if (f.ruleId === 'tokens/duplicate-value') {
    return resolveDuplicateSuggestion(f);
  }

  return null;
}

export function resolveDuplicateSuggestion(f: FindingDoc): ResolvedSuggestion {
  const tokenNamesMatch = f.message.match(
    /:\s*(--[\w-]+(?:,\s*--[\w-]+)+)\s*$/,
  );
  const allTokensMatch = f.message.match(
    /(?:Duplicate tokens:\s*)(--[\w-]+(?:,\s*--[\w-]+)+)/,
  );
  const names = (tokenNamesMatch?.[1] ?? allTokensMatch?.[1])
    ?.split(/,\s*/)
    .filter(Boolean);
  if (!names || names.length < 2) return null;
  const canonical = names[0];
  const others = names.slice(1);
  const display =
    others.length === 1
      ? `Consolidate ${others[0]} into var(${canonical})`
      : `Consolidate ${others.join(', ')} into var(${canonical})`;
  return { kind: 'prose', display, copyText: null };
}

export function extractRepoFromBinding(f: FindingDoc): string | undefined {
  const bid = f.bindingId;
  if (bid && bid.startsWith('ci:')) return bid.slice(3);
  return undefined;
}

export function isFixable(f: FindingDoc): boolean {
  return isSafeAutofixCandidate(f);
}

export function parseFindingAttributes(
  finding: FindingDoc,
): Record<string, unknown> {
  return parseJsonRecord(finding.attributesJson);
}

export function findingFixContext(finding: FindingDoc): string | null {
  return findingFixContextFromJson({
    fixJson: finding.fixJson,
    evidenceJson: finding.evidenceJson,
    intent: finding.intent,
  });
}

export function formatRuleIdentity(finding: FindingDoc): string {
  const ruleLabel = formatRuleLabel(finding.ruleId);
  return finding.code ? `${finding.code} · ${ruleLabel}` : ruleLabel;
}
